import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  PopupRequest,
  PublicClientApplication,
  SilentRequest,
  SsoSilentRequest,
} from "@azure/msal-browser";
import { getAuthEnv } from "./init";
import {
  AuthConfig,
  AuthSource,
  getAuthConfig,
  getScopesByConfig,
} from "@granular/fabric3-definitions";

export const fetchAccessToken = async (
  authApp: PublicClientApplication,
  flow: AuthSource,
  skipCache = false,
  correlationId: string | undefined = undefined,
): Promise<string> => {
  const activeAccount = authApp.getActiveAccount();
  if (!activeAccount) {
    throw new Error("No active account. User must log in.");
  }

  const authConfig: AuthConfig = getAuthConfig(getAuthEnv(), flow);
  const scopes = getScopesByConfig(authConfig);

  // Attempt 1: Try silent token acquisition from cache
  try {
    const silentRequest: SilentRequest = {
      scopes: scopes,
      account: activeAccount,
      correlationId: correlationId,
      forceRefresh: skipCache,
    };

    const result = await authApp.acquireTokenSilent(silentRequest);
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      throw error;
    }
    console.warn("[MSAL] Silent token acquisition failed:", error);
  }

  // Attempt 2: Try SSO silent (if we have a login hint)
  if (activeAccount.loginHint) {
    try {
      const ssoRequest: SsoSilentRequest = {
        scopes: scopes,
        loginHint: activeAccount.loginHint,
        correlationId: correlationId,
      };

      const result = await authApp.ssoSilent(ssoRequest);
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        throw error;
      }
      console.warn("[MSAL] SSO silent token acquisition failed:", error);
    }
  }

  // Attempt 3: Try popup interaction
  try {
    const popupRequest: PopupRequest = {
      scopes: scopes,
      account: activeAccount,
      correlationId: correlationId,
    };

    const result = await authApp.acquireTokenPopup(popupRequest);
    return result.accessToken;
  } catch (error) {
    // Popup was blocked by the browser — re-throw so the caller can redirect
    // immediately with an explicit Datadog log rather than reaching the
    // generic "all attempts failed" path below.
    if (
      error instanceof BrowserAuthError &&
      (error.errorCode === BrowserAuthErrorCodes.popupWindowError ||
        error.errorCode === BrowserAuthErrorCodes.emptyWindowError)
    ) {
      throw error;
    }
    console.warn("[MSAL] Popup token acquisition failed:", error);
  }

  throw new Error(
    "[MSAL] All SPA authentication attempts failed. User needs to login.",
  );
};
